import AppWrapper from "@desk/AppWrapper";
import { GlobalLayout, RootLayout } from "@desk/layout";
import { Dashboard, Login, ObjectsPage, Register } from "@desk/pages";
import { BizkeyApprovalDetail, BizkeyApprovalList } from "@desk/pages/bizkey";
import {
  MansionCustomerConfigureList,
  NotificationSetting,
  ShareOfficeCustomerConfigureList,
} from "@desk/pages/customer-configure";
import { DeliverySettingsList } from "@desk/pages/delivery-settings";
import { DeviceList, DeviceShow } from "@desk/pages/devices-configure";
import { Service503 } from "@desk/pages/errors";
import { LockerDetail, LockersList, ParcelLockersList } from "@desk/pages/lockers-configure";
import { OpsSettingsList, UserDetail } from "@desk/pages/ops-settings";
import { ResidentialList } from "@desk/pages/residential-configure";
import { StrandedList } from "@desk/pages/stranded-list";
import { AccountListPage, SubscriptionsList } from "@desk/pages/system-configure";
import { TempAccessCodeList, TempAccessList } from "@desk/pages/temp-access";
import { Todo } from "@desk/pages/todo";
import { ErrorComponent } from "@refinedev/antd";
import { Authenticated, CanAccess } from "@refinedev/core";
import { NavigateToResource } from "@refinedev/react-router-v6";
import { ConfigProvider } from "antd";
import type { RouteObject } from "react-router-dom";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { ForgotPassword } from "ui/auth";
import { NoAccessError } from "ui/error";
import { v4 } from "uuid";

const app_routes = [
  {
    element: (
      <AppWrapper>
        <Authenticated key={v4()}>
          <Outlet />
        </Authenticated>
      </AppWrapper>
    ),
    children: [
      {
        path: "/select",
        element: (
          <RootLayout Title="Pabbit Desk">
            <ObjectsPage />
          </RootLayout>
        ),
      },
      {
        path: "/",
        element: (
          // @ts-ignore
          <GlobalLayout Title="Pabbit Desk">
            <CanAccess fallback={<NoAccessError />}>
              <Outlet />
            </CanAccess>
          </GlobalLayout>
        ),
        children: [
          { index: true, element: <Navigate to="/select" /> },
          { path: "dashboard", element: <Dashboard /> },
          {
            path: "todo_management",
            element: <Todo />,
          },
          {
            path: "todo_management/completed",
            element: <Todo />,
          },
          { path: "delivery-settings", element: <DeliverySettingsList /> },
          {
            path: "property-personnel",
            element: <OpsSettingsList />,
          },
          { path: "property-personnel/user/:userId", element: <UserDetail /> },
          { path: "strandedList", element: <StrandedList /> },
          {
            path: "devices-config",
            children: [
              { path: "lite", element: <DeviceList /> },
              { path: "dearis", element: <DeviceList /> },
              { path: "dearis+", element: <DeviceList /> },
              { path: "patmo", element: <DeviceList /> },
              { path: "locker", element: <DeviceList /> },
              { path: "locker/:deviceId", element: <DeviceShow /> },
              { path: "notify", element: <DeviceList /> },
            ],
          },
          { path: "customer-configure/mansion", element: <MansionCustomerConfigureList /> },
          { path: "customer-configure/shareoffice", element: <ShareOfficeCustomerConfigureList /> },
          { path: "notification-setting/:propertyType", element: <NotificationSetting /> },
          { path: "temporary-access", element: <TempAccessList /> },
          // { path: "temporary-accessCodeList", element: <TempAccessCodeList /> },
          {
            path: "bizkey",
            children: [
              { path: "approval", element: <BizkeyApprovalList /> },
              {
                path: "approval/:application_id",
                element: <BizkeyApprovalDetail />,
              },
            ],
          },
          {
            path: "lockersConfigure",
            children: [
              {
                index: true,
                element: <NavigateToResource resource="lockers_list" />,
              },
              { path: "lockers", element: <LockersList /> },
              { path: "lockers/:id", element: <LockerDetail /> },
              { path: "parcelLockers", element: <ParcelLockersList /> },
            ],
          },
          { path: "residentialConfigure", element: <ResidentialList /> },
          {
            path: "systemConfigure",
            children: [
              { path: "accounts", element: <AccountListPage /> },
              { path: "subscriptions", element: <SubscriptionsList /> },
            ],
          },
          { path: "*", element: <ErrorComponent /> },
        ],
      },
    ],
  },
  {
    element: (
      <AppWrapper>
        <ConfigProvider
          theme={{
            components: {
              Layout: { bodyBg: "url('/auth_bg.png') no-repeat fixed" },
            },
          }}
        >
          <Authenticated fallback={<Outlet />} key={v4()}>
            <NavigateToResource />
          </Authenticated>
        </ConfigProvider>
      </AppWrapper>
    ),
    children: [
      { path: "/login", element: <Login /> },
      { path: "/register", element: <Register /> },
      { path: "/forgot-password", element: <ForgotPassword /> },
      { path: "/service-unavailable", element: <Service503 /> },
    ],
  },
] satisfies RouteObject[];
export const router = createBrowserRouter(app_routes, { basename: "/desk" });
